import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { EmailSchedule, EmailScheduleRequest } from '../../../models/scheduled-emails/email-schedule';
import { ScheduleFrequency } from '../../../enums/email.enum';

@Injectable()
export class ScheduledEmailsService {
    private headers: HttpHeaders;
    private accessPointUrl: string;

    constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
        this.headers = new HttpHeaders({
            'Content-Type': 'application/json; charset=utf-8',
        });
        this.accessPointUrl = baseUrl + 'api/ScheduleEmail';
    }

    /**
     * Send group analytics email immediately to customer
     * @param groupId Group ID
     * @param customerId Customer ID
     * @returns Observable<boolean>
     */
    sendGroupAnalyticsEmail(groupId: number, customerId: number): Observable<boolean> {
        return this.http.post<boolean>(
            `${this.accessPointUrl}/group-analytics/email/group/${groupId}`,
            customerId,
            { headers: this.headers }
        );
    }

    /**
     * Schedule an email to be sent at a specific time
     * @param emailScheduleRequest Email schedule request data
     * @returns Observable<void>
     */
    scheduleEmail(emailScheduleRequest: EmailScheduleRequest): Observable<void> {
        return this.http.post<void>(
            `${this.accessPointUrl}/group-analytics/schedule-email`,
            emailScheduleRequest,
            { headers: this.headers }
        );
    }

    /**
     * Update a scheduled email
     * @param oid Schedule email ID
     * @param emailScheduleRequest Updated schedule email request
     * @returns Observable<EmailSchedule>
     */
    updateScheduleEmail(oid: number, emailScheduleRequest: EmailScheduleRequest): Observable<EmailSchedule> {
        return this.http.put<EmailSchedule>(
            `${this.accessPointUrl}/${oid}`,
            emailScheduleRequest,
            { headers: this.headers }
        );
    }

    /**
     * Delete a scheduled email
     * @param oid Schedule email ID
     * @returns Observable<void>
     */
    deleteScheduleEmail(oid: number): Observable<void> {
        return this.http.delete<void>(
            `${this.accessPointUrl}/${oid}`,
            { headers: this.headers }
        );
    }

    /**
     * Get all scheduled emails for a specific group
     * @param groupId Group ID
     * @returns Observable<EmailSchedule[]>
     */
    getScheduleEmailsByGroup(groupId: number): Observable<EmailSchedule[]> {
        return this.http.get<EmailSchedule[]>(
            `${this.accessPointUrl}/group/${groupId}`,
            { headers: this.headers }
        );
    }

    /**
     * Get scheduled email by ID (utility method for future use)
     * @param oid Schedule email ID
     * @returns Observable<EmailSchedule>
     */
    getScheduleEmailById(oid: number): Observable<EmailSchedule> {
        return this.http.get<EmailSchedule>(
            `${this.accessPointUrl}/${oid}`,
            { headers: this.headers }
        );
    }

    /**
     * Delete multiple scheduled emails (utility method)
     * @param oids Array of schedule email IDs
     * @returns Observable<void[]>
     */
    deleteMultipleScheduleEmails(oids: number[]): Observable<void[]> {
        const deleteRequests = oids.map(oid => this.deleteScheduleEmail(oid));
        // Use forkJoin to execute all delete requests in parallel
        return forkJoin(deleteRequests);
    }
}
